import React from 'react';
import { useLocation } from 'wouter';
import { Database, Settings, User, Activity, FileText, Server, TestTube, ArrowLeft, Code, Zap, AlertTriangle } from 'lucide-react';
import { Link } from 'wouter';
import { SQL_TOPICS_ANALYSIS, ANALYSIS_CATEGORIES } from '@/lib/sql-analysis-framework';

interface SQLToolPageProps {
  tool: string;
}

export function SQLToolPage({ tool }: SQLToolPageProps) {

  // Helper function to get topics related to this tool
  const getRelatedTopics = (toolName: string) => {
    const toolKeywords = getToolKeywords(toolName);
    return SQL_TOPICS_ANALYSIS.filter(topic =>
      toolKeywords.some(keyword =>
        topic.topic.toLowerCase().includes(keyword.toLowerCase())
      )
    );
  };

  // Helper function to get assessment categories for this tool
  const getAssessmentCategories = (toolName: string) => {
    const relatedTopics = getRelatedTopics(toolName);
    const categories = new Set<string>();
    relatedTopics.forEach(topic => {
      categories.add(topic.primaryCategory);
      topic.secondaryCategories?.forEach(cat => categories.add(cat));
    });
    return Array.from(categories);
  };

  // Map tool names to keywords for topic matching
  const getToolKeywords = (toolName: string): string[] => {
    const keywordMap: Record<string, string[]> = {
      'configurations': ['configuration', 'query store', 'intelligent query'],
      'database-design': ['columnstore', 'memory', 'index', 'statistics'],
      'security': ['threat protection', 'encrypted', 'masking', 'security'],
      'performance': ['performance', 'cpu', 'memory', 'tempdb', 'io', 'concurrency', 'query execution', 'plan', 'monitoring', 'baseline'],
      'error-logs': ['extended events', 'diagnostics', 'event'],
      'alwayson': ['availability', 'distributed', 'disaster recovery'],
      'maintenance': ['maintenance', 'backup'],
      'properties': ['linux', 'container'],
      'excellence': ['machine learning', 'analytics', 'cloud', 'hybrid']
    };
    return keywordMap[toolName] || [];
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'STATIC': return <Code className="w-4 h-4" />;
      case 'LOGICAL': return <Database className="w-4 h-4" />;
      case 'PERFORMANCE': return <Activity className="w-4 h-4" />;
      case 'LOAD': return <Zap className="w-4 h-4" />;
      case 'STRESS': return <AlertTriangle className="w-4 h-4" />;
      default: return <Settings className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'STATIC': return 'bg-green-100 text-green-800 border-green-200';
      case 'LOGICAL': return 'bg-shiftleft-100 text-shiftleft-800 border-shiftleft-200';
      case 'PERFORMANCE': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'LOAD': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'STRESS': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  interface AdvancedTopic {
    title: string;
    description: string;
  }

  interface ToolConfig {
    title: string;
    icon: React.ReactNode;
    description: string;
    features: string[];
    advancedTopics?: AdvancedTopic[];
  }

  const getToolConfig = (tool: string): ToolConfig => {
    switch (tool) {
      case 'configurations':
        return {
          title: 'SQL Server Configurations',
          icon: <Settings className="w-6 h-6" />,
          description: 'Manage SQL Server instance configurations, settings, and parameters.',
          features: [
            'Server Configuration Options',
            'Database Engine Settings',
            'Memory Configuration',
            'Security Settings',
            'Advanced Options',
            'Configuration Validation'
          ],
          advancedTopics: [
            { title: 'Advanced Query Store Configuration', description: 'Configure Query Store for optimal query performance tracking and automatic plan correction.' },
            { title: 'Intelligent Query Processing', description: 'Leverage adaptive query processing, batch mode operations, and memory grant feedback.' },
            { title: 'SQL Server Architecture Scheduling and Waits', description: 'Deep dive into SQL Server scheduler internals, wait types analysis, and advanced troubleshooting of performance bottlenecks.' }
          ]
        };
      case 'database-design':
        return {
          title: 'Database Design & Options',
          icon: <Database className="w-6 h-6" />,
          description: 'Design and configure database schemas, tables, and relationships.',
          features: [
            'Schema Design Tools',
            'Table Structure Analysis',
            'Index Recommendations',
            'Relationship Mapping',
            'Data Type Optimization',
            'Normalization Guidelines'
          ],
          advancedTopics: [
            { title: 'Columnstore Index Optimization', description: 'Design and optimize columnstore indexes for analytical workloads and data warehousing.' },
            { title: 'In-Memory OLTP (Hekaton)', description: 'Implement memory-optimized tables and natively compiled stored procedures for high-performance OLTP.' },
            { title: 'Statistics and Index Internals', description: 'Deep dive into index structures, B-tree internals, statistics histograms, and cardinality estimation algorithms.' },
            { title: 'Index Tuning', description: 'Master advanced index design, covering strategies, missing index analysis, and index consolidation techniques.' }
          ]
        };
      case 'security':
        return {
          title: 'SQL Server Security',
          icon: <User className="w-6 h-6" />,
          description: 'Configure security settings, users, and access permissions.',
          features: [
            'User Management',
            'Role-Based Access Control',
            'Permission Auditing',
            'Authentication Methods',
            'Security Policies',
            'Compliance Monitoring'
          ],
          advancedTopics: [
            { title: 'Advanced Threat Protection', description: 'Implement SQL ATP for threat detection, vulnerability assessment, and data discovery & classification.' },
            { title: 'Always Encrypted & Dynamic Data Masking', description: 'Secure sensitive data with encryption and masking technologies for compliance and privacy.' }
          ]
        };
      case 'performance':
        return {
          title: 'SQL Performance Monitoring',
          icon: <Activity className="w-6 h-6" />,
          description: 'Monitor and optimize SQL Server performance metrics.',
          features: [
            'Query Performance Analysis',
            'Wait Statistics',
            'Resource Utilization',
            'Performance Counters',
            'Blocking Analysis',
            'Execution Plans'
          ],
          advancedTopics: [
            { title: 'SQL Server Architecture Scheduling and Waits', description: 'Deep dive into SQL Server scheduler internals, wait types analysis, and advanced troubleshooting of performance bottlenecks.' },
            { title: 'CPU Performance Monitoring & Tuning', description: 'Master CPU utilization analysis, query parallelism optimization, and advanced CPU performance troubleshooting techniques.' },
            { title: 'SQL Server Tempdb Internals and Tuning', description: 'Optimize tempdb configuration, understand allocation algorithms, and resolve tempdb contention issues.' },
            { title: 'SQL Server IO Internals and Tuning', description: 'Master IO subsystem optimization, storage performance analysis, and advanced IO troubleshooting methodologies.' },
            { title: 'SQL Server Memory Internals and Troubleshooting', description: 'Deep understanding of memory management, buffer pool optimization, and memory pressure troubleshooting.' },
            { title: 'Concurrency & Transactions', description: 'Advanced transaction isolation levels, locking mechanisms, deadlock resolution, and concurrency optimization strategies.' },
            { title: 'Query Execution & Query Plan Analysis', description: 'Master execution plan analysis, operator performance evaluation, and advanced query optimization techniques.' },
            { title: 'Query Tuning', description: 'Advanced query optimization strategies, parameter sniffing solutions, and complex query performance troubleshooting.' },
            { title: 'Plan Caching & Recompilation', description: 'Understand plan cache internals, recompilation triggers, and optimize plan reuse for maximum performance.' },
            { title: 'Monitoring Tracing & Baselining', description: 'Establish performance baselines, implement comprehensive monitoring strategies, and create effective alerting systems.' }
          ]
        };
      case 'error-logs':
        return {
          title: 'Error Log Analysis',
          icon: <FileText className="w-6 h-6" />,
          description: 'Analyze SQL Server error logs and diagnostic information.',
          features: [
            'Error Log Parsing',
            'Critical Error Detection',
            'Log File Management',
            'Alert Configuration',
            'Historical Analysis',
            'Automated Monitoring'
          ],
          advancedTopics: [
            { title: 'Extended Events & Advanced Diagnostics', description: 'Master Extended Events for advanced troubleshooting, custom event tracking, and performance analysis.' },
            { title: 'Extended Events', description: 'Advanced Extended Events implementation for lightweight monitoring, custom event sessions, and real-time performance tracking.' }
          ]
        };
      case 'alwayson':
        return {
          title: 'AlwaysOn Availability Groups',
          icon: <Activity className="w-6 h-6" />,
          description: 'Monitor and manage AlwaysOn Availability Groups health.',
          features: [
            'AG Health Monitoring',
            'Replica Status',
            'Failover Management',
            'Synchronization Status',
            'Performance Metrics',
            'Disaster Recovery'
          ],
          advancedTopics: [
            { title: 'Distributed Availability Groups', description: 'Configure and manage distributed AGs across multiple data centers and cloud regions.' },
            { title: 'Advanced HA/DR Strategies', description: 'Implement complex high availability and disaster recovery patterns with cross-platform support.' }
          ]
        };
      case 'maintenance':
        return {
          title: 'SQL Server Maintenance Checklist',
          icon: <Server className="w-6 h-6" />,
          description: 'Comprehensive maintenance checklist for busy SQL Server administrators to keep your systems performing at their best.',
          features: [
            'Daily Maintenance Tasks',
            'Weekly Optimization Tasks',
            'Monthly Health Checks',
            'Quarterly Reviews',
            'Troubleshooting Scenarios',
            'Automated Monitoring'
          ],
          advancedTopics: [
            { title: 'Daily Tasks', description: 'Check SQL Server Agent jobs, review database backups, monitor disk space and database growth, analyze performance metrics, and scan error logs.' },
            { title: 'Weekly Tasks', description: 'Rebuild or reorganize indexes, update statistics, audit SQL Server jobs and alerts, review resource usage, and check for blocking or deadlocks.' },
            { title: 'Monthly Tasks', description: 'Run DBCC CHECKDB, test backup restores, apply security updates, clean up logs and old data, and optimize queries.' },
            { title: 'Quarterly Tasks', description: 'Audit user permissions, test disaster recovery plan, review SQL Server configuration, perform capacity planning, and evaluate index usage.' },
            { title: 'Troubleshooting Scenarios', description: 'Handle SQL Server startup failures, query timeout errors, transaction log full errors, high memory usage, and slow tempdb performance.' },
            { title: 'Best Practices', description: 'Implement backup and restore strategies, index maintenance, database integrity checks, performance monitoring, security compliance, and disk space management.' }
          ]
        };
      case 'properties':
        return {
          title: 'Instance Properties',
          icon: <Settings className="w-6 h-6" />,
          description: 'View and configure SQL Server instance properties.',
          features: [
            'Instance Information',
            'Service Configuration',
            'Network Settings',
            'File Locations',
            'Resource Limits',
            'Version Details'
          ],
          advancedTopics: [
            { title: 'SQL Server on Linux & Containers', description: 'Deploy and manage SQL Server on Linux platforms, Docker containers, and Kubernetes orchestration.' }
          ]
        };
      case 'excellence':
        return {
          title: 'Operational Excellence',
          icon: <TestTube className="w-6 h-6" />,
          description: 'Best practices and operational excellence guidelines.',
          features: [
            'Best Practice Analyzer',
            'Health Check Reports',
            'Performance Baselines',
            'Configuration Validation',
            'Operational Guidelines',
            'Continuous Improvement'
          ],
          advancedTopics: [
            { title: 'Machine Learning & Advanced Analytics', description: 'Leverage SQL Server ML Services, R, Python, and advanced analytics for intelligent data processing.' },
            { title: 'Cloud Hybrid & Azure Integration', description: 'Master hybrid cloud scenarios with Azure SQL, managed instances, and seamless cloud integration.' }
          ]
        };
      default:
        return {
          title: 'SQL Tools',
          icon: <Database className="w-6 h-6" />,
          description: 'SQL Server administration and management tools.',
          features: []
        };
    }
  };

  const config = getToolConfig(tool);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link href="/">
              <button className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to Dashboard</span>
              </button>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <div className="p-3 bg-shiftleft-100 dark:bg-shiftleft-900 rounded-lg text-shiftleft-600 dark:text-shiftleft-400">
              {config.icon}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{config.title}</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{config.description}</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {config.features.map((feature) => (
            <div key={feature} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{feature}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Configure and manage {feature.toLowerCase()} for optimal SQL Server performance.
              </p>
            </div>
          ))}
        </div>

        {/* Advanced Topics Section */}
        {config.advancedTopics && config.advancedTopics.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <TestTube className="w-6 h-6 text-shiftleft-600 dark:text-shiftleft-400" />
              Advanced Topics
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {config.advancedTopics.map((topic) => (
                <div key={topic.title} className="bg-shiftleft-50 dark:bg-shiftleft-900/20 p-6 rounded-lg border border-shiftleft-200 dark:border-shiftleft-800">
                  <h3 className="font-bold text-shiftleft-900 dark:text-shiftleft-100 mb-3 text-lg">{topic.title}</h3>
                  <p className="text-shiftleft-700 dark:text-shiftleft-300 text-sm leading-relaxed">{topic.description}</p>
                  <div className="mt-4 inline-flex items-center text-xs text-shiftleft-600 dark:text-shiftleft-400 font-medium">
                    <span className="w-2 h-2 bg-shiftleft-600 dark:bg-shiftleft-400 rounded-full mr-2"></span>
                    Advanced Level
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assessment Methodology Section */}
        {getRelatedTopics(tool).length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
              Assessment Methodology
            </h2>

            {/* Assessment Categories */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Analysis Approaches</h3>
              <div className="flex flex-wrap gap-3 mb-6">
                {getAssessmentCategories(tool).map(category => (
                  <div key={category} className={`px-3 py-2 rounded-lg border flex items-center gap-2 ${getCategoryColor(category)}`}>
                    {getCategoryIcon(category)}
                    <span className="font-medium text-sm">{ANALYSIS_CATEGORIES[category]?.name || category}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Topics with Assessment Methods */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Assessment Methods by Topic</h3>
              {getRelatedTopics(tool).map((topic, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white text-lg">{topic.topic}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(topic.primaryCategory)}`}>
                          Primary: {ANALYSIS_CATEGORIES[topic.primaryCategory]?.name}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs border ${topic.complexity === 'Expert' ? 'bg-red-50 text-red-700 border-red-200' :
                            topic.complexity === 'Advanced' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                              topic.complexity === 'Intermediate' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                'bg-green-50 text-green-700 border-green-200'
                          }`}>
                          {topic.complexity}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs border ${topic.automationPotential === 'High' ? 'bg-shiftleft-50 text-shiftleft-700 border-shiftleft-200' :
                            topic.automationPotential === 'Medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                              'bg-gray-50 text-gray-700 border-gray-200'
                          }`}>
                          {topic.automationPotential} Automation
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Assessment Methods */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white mb-2">Assessment Methods</h5>
                      {Object.entries(topic.assessmentMethods).map(([method, approaches]) =>
                        approaches && approaches.length > 0 && (
                          <div key={method} className="mb-3">
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium mb-2 ${getCategoryColor(method.toUpperCase())}`}>
                              {getCategoryIcon(method.toUpperCase())}
                              {method.charAt(0).toUpperCase() + method.slice(1)}
                            </div>
                            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                              {approaches.map((approach, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-shiftleft-400 rounded-full mt-2 flex-shrink-0"></span>
                                  {approach}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                      )}
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white mb-2">Required Artifacts</h5>
                      <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        {(topic.requiredArtifacts || []).map((artifact, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                            {artifact}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Maintenance Checklist Section - Only show for maintenance tool */}
        {tool === 'maintenance' && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Server className="w-6 h-6 text-green-600 dark:text-green-400" />
              SQL Server Maintenance Checklist
            </h2>

            {/* Daily Tasks */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                Daily Maintenance Tasks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { task: 'Check SQL Server Agent Jobs', desc: 'Ensure all scheduled jobs (backups, index maintenance) have run successfully' },
                  { task: 'Review Database Backups', desc: 'Verify the success of all backups and check storage locations' },
                  { task: 'Monitor Disk Space', desc: 'Check available disk space and monitor for unexpected database growth' },
                  { task: 'Analyze Performance Metrics', desc: 'Review server CPU, memory, and I/O usage to detect bottlenecks' },
                  { task: 'Scan Error Logs', desc: 'Look for critical errors or warnings in SQL Server and Windows Event logs' }
                ].map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border dark:border-gray-700">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.task}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekly Tasks */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-shiftleft-500 rounded-full"></div>
                Weekly Maintenance Tasks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { task: 'Rebuild or Reorganize Indexes', desc: 'Address index fragmentation to maintain query performance' },
                  { task: 'Update Statistics', desc: 'Refresh outdated statistics to improve query execution plans' },
                  { task: 'Audit SQL Server Jobs', desc: 'Review scheduled jobs for effectiveness and ensure alerts are configured' },
                  { task: 'Review Resource Usage', desc: 'Identify patterns or anomalies in CPU, memory, and disk usage' },
                  { task: 'Check for Blocking/Deadlocks', desc: 'Investigate and resolve queries causing contention in the system' }
                ].map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border dark:border-gray-700">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.task}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Tasks */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                Monthly Maintenance Tasks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { task: 'Run DBCC CHECKDB', desc: 'Check database integrity to detect and address corruption' },
                  { task: 'Test Backup Restores', desc: 'Restore backups in non-production to verify integrity and reliability' },
                  { task: 'Apply Security Updates', desc: 'Install the latest patches for SQL Server and the underlying OS' },
                  { task: 'Clean Up Logs and Old Data', desc: 'Archive or delete old data and transaction logs to free up space' },
                  { task: 'Optimize Queries', desc: 'Review execution plans for slow queries and optimize as needed' }
                ].map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border dark:border-gray-700">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.task}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quarterly Tasks */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                Quarterly Maintenance Tasks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { task: 'Audit User Permissions', desc: 'Review and update access controls to minimize security risks' },
                  { task: 'Test Disaster Recovery Plan', desc: 'Simulate failover and recovery scenarios to ensure readiness' },
                  { task: 'Review SQL Server Configuration', desc: 'Compare settings with best practices and update if necessary' },
                  { task: 'Perform Capacity Planning', desc: 'Analyze growth trends to forecast storage, CPU, and memory needs' },
                  { task: 'Evaluate Index Usage', desc: 'Identify unused or poorly performing indexes and adjust accordingly' }
                ].map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border dark:border-gray-700">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.task}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Troubleshooting Scenarios */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Common Troubleshooting Scenarios
              </h3>
              <div className="space-y-4">
                {[
                  {
                    scenario: 'SQL Server Fails to Start',
                    symptoms: 'SQL Server service fails to start or stops unexpectedly',
                    causes: 'Missing/corrupted system databases, insufficient disk space for tempdb, permission issues',
                    solutions: 'Check logs, verify database files, restore system databases, free disk space'
                  },
                  {
                    scenario: 'Query Timeout Errors',
                    symptoms: 'Applications report query timeout errors',
                    causes: 'Long-running queries, blocking/deadlocks, high resource contention',
                    solutions: 'Identify problem queries, analyze execution plans, monitor blocking and deadlocks'
                  },
                  {
                    scenario: 'Transaction Log is Full',
                    symptoms: 'Database operations fail with "Transaction log full" error',
                    causes: 'Log file not being truncated, large uncommitted transactions, insufficient disk space',
                    solutions: 'Check recovery model, backup transaction log, shrink log file if needed'
                  },
                  {
                    scenario: 'High Memory Usage',
                    symptoms: 'SQL Server consumes all available system memory',
                    causes: 'Inappropriate max memory settings, memory leaks in queries',
                    solutions: 'Adjust max memory settings, check memory consumers, optimize queries'
                  },
                  {
                    scenario: 'Slow Tempdb Performance',
                    symptoms: 'Queries using tempdb are unusually slow',
                    causes: 'Disk contention on tempdb storage, insufficient tempdb files',
                    solutions: 'Optimize tempdb files (one per CPU core), move tempdb to faster storage'
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                    <h4 className="font-bold text-red-900 dark:text-red-100 mb-2">{item.scenario}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-semibold text-red-800 dark:text-red-200">Symptoms:</span>
                        <p className="text-red-700 dark:text-red-300 mt-1">{item.symptoms}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-red-800 dark:text-red-200">Root Causes:</span>
                        <p className="text-red-700 dark:text-red-300 mt-1">{item.causes}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-red-800 dark:text-red-200">Solutions:</span>
                        <p className="text-red-700 dark:text-red-300 mt-1">{item.solutions}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h3>
              <div className="space-y-4">
                {[
                  { q: 'How often should I run DBCC CHECKDB?', a: 'Ideally, run DBCC CHECKDB weekly or monthly, depending on the size and importance of the database.' },
                  { q: 'What should I do if backups are failing?', a: 'Start by checking the SQL Server Agent job logs for errors. Common causes include insufficient disk space, network issues, or file permission problems.' },
                  { q: 'How do I handle frequent deadlocks?', a: 'Use SQL Server\'s Extended Events or Deadlock Graph to identify the queries causing contention and optimize them or adjust indexing strategies.' },
                  { q: 'When should I rebuild vs. reorganize indexes?', a: 'Reorganize indexes if fragmentation is between 5-30%. Rebuild indexes if fragmentation exceeds 30%.' },
                  { q: 'Can I automate these tasks?', a: 'Yes, many tasks can be automated using SQL Server Agent jobs, PowerShell scripts, or a dedicated monitoring solution.' }
                ].map((item, index) => (
                  <div key={index} className="bg-shiftleft-50 dark:bg-shiftleft-900/20 border border-shiftleft-200 dark:border-shiftleft-800 rounded-lg p-4">
                    <h4 className="font-semibold text-shiftleft-900 dark:text-shiftleft-100 mb-2">{item.q}</h4>
                    <p className="text-shiftleft-700 dark:text-shiftleft-300 text-sm">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Coming Soon Notice - Show for other tools */}
        {tool !== 'maintenance' && (
          <div className="bg-shiftleft-50 dark:bg-shiftleft-900/20 border border-shiftleft-200 dark:border-shiftleft-800 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-shiftleft-100 dark:bg-shiftleft-900 rounded-full">
                <TestTube className="w-5 h-5 text-shiftleft-600 dark:text-shiftleft-400" />
              </div>
              <div>
                <h3 className="font-semibold text-shiftleft-900 dark:text-shiftleft-100">Coming Soon</h3>
                <p className="text-shiftleft-700 dark:text-shiftleft-300 text-sm">
                  This tool is currently under development. Advanced SQL Server management capabilities will be available in the next release.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SQLToolPage;